import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DataTableColumn<T = any> {
  key: string;
  label: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface DataTableGroup<T = any> {
  key: string;
  label: string;
  items: T[];
  defaultExpanded?: boolean;
}

export interface DataTableConfig {
  showFilters?: boolean;
  showCheckboxes?: boolean;
  showGrouping?: boolean;
  showFooter?: boolean;
  compact?: boolean;
}

export interface DataTableProps<T = any> {
  title: string;
  description?: string;
  data: T[];
  columns: DataTableColumn<T>[];
  groups?: DataTableGroup<T>[];
  groupBy?: (item: T) => string;
  getGroupLabel?: (groupKey: string) => string;
  filterArea?: React.ReactNode;
  actionsArea?: React.ReactNode;
  rowActions?: (item: T) => React.ReactNode;
  onSelectionChange?: (selectedItems: T[]) => void;
  config?: DataTableConfig;
  className?: string;
  emptyMessage?: string;
  getItemId?: (item: T) => string;
}

export function DataTable<T = any>({
  title,
  description,
  data,
  columns,
  groups,
  groupBy,
  getGroupLabel,
  filterArea,
  actionsArea,
  rowActions,
  onSelectionChange,
  config = {},
  className,
  emptyMessage = 'Nenhum item encontrado',
  getItemId = (item: any) => item.id || String(item),
}: DataTableProps<T>) {
  const {
    showFilters = false,
    showCheckboxes = false,
    showGrouping = false,
    showFooter = true,
    compact = false,
  } = config;

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Inicializar grupos expandidos
  React.useEffect(() => {
    if (groups) {
      const initialExpanded: Record<string, boolean> = {};
      groups.forEach((group) => {
        initialExpanded[group.key] = group.defaultExpanded ?? false;
      });
      setExpandedGroups(initialExpanded);
    } else if (groupBy) {
      // Se usar groupBy, expandir o primeiro grupo por padrão
      const grouped = groupData(data, groupBy);
      const firstGroup = Object.keys(grouped)[0];
      if (firstGroup) {
        setExpandedGroups({ [firstGroup]: true });
      }
    }
  }, [groups, groupBy, data]);

  const groupData = (items: T[], groupFn: (item: T) => string): Record<string, T[]> => {
    return items.reduce((acc, item) => {
      const key = groupFn(item);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {} as Record<string, T[]>);
  };

  const groupedData = useMemo(() => {
    if (groups) {
      return groups;
    }
    if (groupBy) {
      const grouped = groupData(data, groupBy);
      return Object.entries(grouped).map(([key, items]) => ({
        key,
        label: getGroupLabel ? getGroupLabel(key) : key,
        items,
        defaultExpanded: false,
      }));
    }
    return null;
  }, [groups, groupBy, data, getGroupLabel]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(data.map(getItemId));
      setSelectedItems(allIds);
      onSelectionChange?.(data);
    } else {
      setSelectedItems(new Set());
      onSelectionChange?.([]);
    }
  };

  const toggleSelectItem = (item: T, checked: boolean) => {
    const itemId = getItemId(item);
    const newSelected = new Set(selectedItems);
    
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    
    setSelectedItems(newSelected);
    
    const selectedData = data.filter((item) => newSelected.has(getItemId(item)));
    onSelectionChange?.(selectedData);
  };

  const isAllSelected = data.length > 0 && selectedItems.size === data.length;

  const displayData = groupedData || [{ key: 'all', label: 'Todos', items: data, defaultExpanded: true }];

  const totalItems = data.length;
  const selectedCount = selectedItems.size;
  const visibleItems = groupedData
    ? Object.values(expandedGroups).filter(Boolean).length > 0
      ? displayData
          .filter((group) => expandedGroups[group.key])
          .reduce((sum, group) => sum + group.items.length, 0)
      : 0
    : totalItems;

  return (
    <Card className={className}>
      <CardHeader className={compact ? 'pb-3' : ''}>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className={compact ? 'text-lg' : 'text-2xl'}>{title}</CardTitle>
            {description && (
              <p className={cn('text-muted-foreground', compact ? 'text-sm' : 'text-base', 'mt-1')}>
                {description}
              </p>
            )}
          </div>
          {actionsArea && <div className="flex gap-2">{actionsArea}</div>}
        </div>
      </CardHeader>
      <CardContent className={compact ? 'p-0' : ''}>
        {/* Área de Filtros */}
        {showFilters && filterArea && (
          <div className="mb-4 p-4 border-b">{filterArea}</div>
        )}

        {data.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={compact ? 'h-9' : ''}>
                  {showCheckboxes && (
                    <TableHead className={cn(compact ? 'h-9 py-1.5 px-3' : '', 'w-12')}>
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Selecionar todos"
                      />
                    </TableHead>
                  )}
                  {columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className={cn(
                        compact ? 'h-9 py-1.5 px-3 text-xs font-semibold' : '',
                        column.headerClassName
                      )}
                    >
                      {column.label}
                    </TableHead>
                  ))}
                  {rowActions && (
                    <TableHead className={cn(compact ? 'h-9 py-1.5 px-3 text-right text-xs font-semibold' : 'text-right')}>
                      Ações
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayData.map((group) => {
                  const isExpanded = expandedGroups[group.key] ?? group.defaultExpanded ?? false;
                  const groupCount = group.items.length;

                  return (
                    <React.Fragment key={group.key}>
                      {/* Linha Agrupadora */}
                      {showGrouping && (
                        <TableRow className={cn(compact ? 'h-9' : '', 'bg-muted/30 hover:bg-muted/40')}>
                          <TableCell
                            colSpan={columns.length + (showCheckboxes ? 1 : 0) + (rowActions ? 1 : 0)}
                            className={cn(compact ? 'py-2 px-3' : '')}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(compact ? 'h-6 w-6 p-0' : '')}
                                  onClick={() => toggleGroup(group.key)}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
                                  ) : (
                                    <ChevronRight className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
                                  )}
                                </Button>
                                <span className={cn('font-semibold', compact ? 'text-xs' : 'text-sm')}>
                                  {group.label} ({groupCount})
                                </span>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Linhas de Detalhes */}
                      {isExpanded &&
                        group.items.map((item, index) => {
                          const itemId = getItemId(item);
                          const isSelected = selectedItems.has(itemId);

                          return (
                            <TableRow
                              key={itemId}
                              className={cn(
                                compact ? 'h-10' : '',
                                'hover:bg-muted/50',
                                isSelected && 'bg-muted/30'
                              )}
                            >
                              {showCheckboxes && (
                                <TableCell className={cn(compact ? 'py-1.5 px-3' : '')}>
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={(checked) => toggleSelectItem(item, checked as boolean)}
                                    aria-label={`Selecionar item ${itemId}`}
                                  />
                                </TableCell>
                              )}
                              {columns.map((column) => (
                                <TableCell
                                  key={column.key}
                                  className={cn(
                                    compact ? 'py-1.5 px-3' : '',
                                    column.className
                                  )}
                                >
                                  {column.render
                                    ? column.render(item, index)
                                    : (item as any)[column.key]}
                                </TableCell>
                              ))}
                              {rowActions && (
                                <TableCell className={cn(compact ? 'py-1.5 px-3 text-right' : 'text-right')}>
                                  <div className="flex justify-end gap-1.5">
                                    {rowActions(item)}
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
              {showFooter && (
                <TableFooter>
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (showCheckboxes ? 1 : 0) + (rowActions ? 1 : 0)}
                      className={cn(compact ? 'py-2 px-3 text-xs' : 'text-sm', 'text-muted-foreground')}
                    >
                      {selectedCount > 0 ? (
                        <span>
                          {selectedCount} de {totalItems} item{selectedCount !== 1 ? 's' : ''} selecionado
                          {selectedCount !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span>
                          {visibleItems} de {totalItems} item{totalItems !== 1 ? 's' : ''} visível
                          {totalItems !== 1 ? 'is' : ''}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

